import { bind as bound } from 'bind-decorator';
import { action, observable, runInAction, when } from 'mobx';
import { computedFn } from 'mobx-utils';
import Api from 'tm3-core/api';
import { cache } from 'tm3-core/cache';
import { ModuleFeatureObservable } from 'tm3-core/controls/module/provider';
import ModuleEnum from 'tm3-core/licence';

export class ProntoCache {
  // Real (empty) cache objects rather than `{}` casts, so consumers can always call
  // getServiceName/getAll/getById even before (or without) a successful load.
  @observable prontoNetworkServices: ProntoNetworkServices = new ProntoNetworkServices();
  @observable prontoInvoiceTemplates: ProntoInvoiceTemplates = new ProntoInvoiceTemplates();

  private cancelPendingLoad?: () => void;

  @action.bound
  onConfigure() {
    this.cancelPendingLoad?.();
    this.cancelPendingLoad = undefined;

    if (cache.hasAllowedModule(ModuleEnum.ProntoReferrals)) {
      return this.load();
    }

    // Licence modules load asynchronously after login and can still be empty when this runs.
    // Load as soon as the module appears instead of skipping the cache for the whole session.
    // Read the observable store directly: cache.hasAllowedModule is an @action.bound method,
    // and MobX actions run untracked, so a `when` built on it would never re-evaluate.
    this.cancelPendingLoad = when(
      () => ModuleFeatureObservable.allowedModules.includes(ModuleEnum.ProntoReferrals),
      () => {
        this.cancelPendingLoad = undefined;
        this.load().catch((e) => console.error('[ProntoCache] deferred load failed', e));
      }
    );
    return Promise.resolve(true);
  }

  private load() {
    return new Promise((resolve, reject) => {
      Api.post('/api/pronto/cache/load')
        .then((res) => {
          const { result } = res.body;

          let networkServices = new ProntoNetworkServices();
          let invoiceTemplates = new ProntoInvoiceTemplates();

          networkServices.onLoad(result.services);
          invoiceTemplates.onLoad(result.invoices);

          runInAction(() => {
            this.prontoNetworkServices = networkServices;
            this.prontoInvoiceTemplates = invoiceTemplates;
          });

          resolve({
            prontoNetworkServices: networkServices,
            prontoInvoiceTemplates: invoiceTemplates
          });
        })
        .catch(reject);
    });
  }
}

class CacheObject {
  @observable loading = true;
  all = observable.map();

  @action.bound
  onLoad(res) {
    this.all.replace(res || {});
    this.loading = false;
  }

  getById = computedFn(this._getById);

  @bound
  _getById(id) {
    return this.all.get(id);
  }
}

class ProntoNetworkServices extends CacheObject {
  public orderBy: string = 'discipline';
  getServiceName = computedFn(this._getServiceName);
  getAll = computedFn(this._getAll);

  @bound
  _getServiceName(networkServiceId) {
    const service = this.getById(networkServiceId);

    const prontoServiceName =
      !!service && service.serviceName ? service.serviceName : 'Unknown Service';

    return prontoServiceName;
  }

  @bound
  _getAll() {
    let services: any[] = [];

    const res = Array.from(this.all._data.values()) || [];
    res.forEach((service) => {
      services.push(service.value);
    });

    const { orderBy } = this;
    try {
      if (orderBy.length > 0) {
        services.sort((a, b) => {
          if (!a[orderBy] || !b[orderBy]) {
            return 0;
          }
          return a[orderBy].localeCompare(b[orderBy]);
        });
      }
    } catch {
      // no ordering is going to happen
      console.error('A problem arose when attempting to order these items');
    }
    return services;
  }
}
class ProntoInvoiceTemplates extends CacheObject {}

export default new ProntoCache();
